'use client';

import React, { useState } from 'react';
import { Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { getUserStores } from '@/api/appuser.api';
import { registerTeamToOrganization } from '@/api/organization.api';
import { useIndicatorStore } from '@/stores/indicator.store';
import { useOrganizationTeamStore } from '@/stores/organizationTeam.store';
import { useToastStore } from '@/stores/toast.store';
import BaseModal from '@/components/modal/BaseModal';

interface TeamRegistrationModalProps {
  showModal: boolean;
  handleShowModal: () => void;
  storeName: string;
  storeId: number;
  teamAuthId: string;
  organizationId: string;
  organizationName: string;
  onRegisterComplete?: (teamName: string) => void;
}

const TeamRegistrationModal = ({ showModal, handleShowModal, storeName, storeId, teamAuthId, organizationId, organizationName, onRegisterComplete }: TeamRegistrationModalProps) => {
  const { setShowToast, setToastInfos } = useToastStore();
  const { setShowIndicator } = useIndicatorStore((state) => state);
  const [teamName, setTeamName] = useState(storeName);
  const { organizations } = useOrganizationTeamStore();
  const setOrganizationTeam = useOrganizationTeamStore((state) => state.setOrganizationTeam);

  const handleRegisterTeam = async () => {
    if (!teamName.trim()) {
      setToastInfos({ text: '팀 이름을 입력해주세요.' });
      setShowToast(true);
      return;
    }

    if (!organizationId) {
      setToastInfos({ text: '조직 정보가 없습니다.' });
      setShowToast(true);
      return;
    }

    try {
      setShowIndicator(true);
      const result = await registerTeamToOrganization(organizationId, teamAuthId, storeId);

      if (!result.isSuccess) {
        setToastInfos({ text: result.message || '팀 등록에 실패했습니다.' });
        setShowToast(true);
        return;
      }

      // 성공 메시지
      setToastInfos({ text: '팀이 성공적으로 등록되었습니다.' });
      setShowToast(true);

      // 성공 콜백 호출
      if (onRegisterComplete) {
        onRegisterComplete(teamName);
      }

      // 모달 닫기
      handleShowModal();
    } catch (error) {
      console.error('팀 등록 중 오류 발생:', error);
      setToastInfos({ text: '팀 등록 중 오류가 발생했습니다.' });
      setShowToast(true);
    } finally {
      setShowIndicator(false);
    }
  };

  return (
    <BaseModal
      showModal={showModal}
      handleShowModal={handleShowModal}
      modalTitle="팀 등록"
      buttons={{
        primary: {
          title: '팀 등록하기',
          onClick: handleRegisterTeam
        }
      }}
      style={{ maxWidth: '446px' }}
    >
      <Stack gap="16px" width="100%" padding="0 20px 20px">
        <Typography variant="subtitle1">
          <strong>{organizationName}</strong> 조직에 <strong>{storeName}</strong> 병원을 등록합니다.
        </Typography>
      </Stack>
    </BaseModal>
  );
};

export default TeamRegistrationModal;
